import type { CSSProperties, ReactNode, RefObject } from "react";
import { useRef } from "react";

import type { CdropdownProps } from "./Cdropdown.types";
import {
  CDROPDOWN_ALIGN,
  CDROPDOWN_MODE,
  CDROPDOWN_OPEN_DIRECTION,
  DEFAULT_DROPDOWN_MAX_WIDTH_PX,
} from "./constants";
import type { DropdownSurfaceProps } from "./DropdownSurface";
import { useCdropdownState } from "./useCdropdownState";
import { useDropdownInteractions } from "./useDropdownInteractions";
import { useDropdownOptionsViewport } from "./useDropdownOptionsViewport";
import { useDropdownPositioning } from "./useDropdownPositioning";

interface CdropdownControllerResult {
  onTriggerClick: () => void;
  shouldRenderSurface: boolean;
  shouldRenderTrigger: ReactNode;
  surfaceProps: Omit<DropdownSurfaceProps, "dataElScope">;
  trigger: ReactNode;
  triggerRef: RefObject<HTMLDivElement | null>;
  triggerStyle: CSSProperties;
}

export function useCdropdownController({
  trigger,
  options,
  isOpen: controlledIsOpen,
  onOpenChange,
  mode = CDROPDOWN_MODE.Trigger,
  anchorRect,
  align = CDROPDOWN_ALIGN.Right,
  width = "13.5rem",
  maxWidth = DEFAULT_DROPDOWN_MAX_WIDTH_PX,
  maxHeight,
  minHeight,
  openDirection = CDROPDOWN_OPEN_DIRECTION.Auto,
  keyboardNavigation = true,
  activeIndex: controlledActiveIndex,
  selectedKey = null,
  onActiveIndexChange,
  closeOnSelect = true,
  dismissOnOutsideClick = true,
  animateClose = true,
  triggerDisabled = false,
  triggerFullWidth = false,
  scrollFadeSize,
  scrollVerticalFadeTopSize,
  scrollVerticalFadeBottomSize,
  scrollVerticalFadeMiddleOpacity,
  anchorRef,
  matchAnchorWidth = false,
}: CdropdownProps): CdropdownControllerResult {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownState = useCdropdownState({
    controlledIsOpen,
    onOpenChange,
    controlledActiveIndex,
    onActiveIndexChange,
    options,
  });
  const optionsLength = options.length;
  const positioning = useDropdownPositioning({
    isOpen: dropdownState.isOpen,
    mode,
    anchorRect,
    triggerRef,
    anchorRef,
    align,
    width,
    maxWidth,
    matchAnchorWidth,
    maxHeight,
    openDirection,
    optionsLength,
  });
  const viewport = useDropdownOptionsViewport({
    isOpen: dropdownState.isOpen,
    options,
    effectiveActiveIndex: dropdownState.effectiveActiveIndex,
    maxHeight,
    minHeight,
    optionsLength,
  });
  const interactions = useDropdownInteractions({
    isOpen: dropdownState.isOpen,
    setIsOpen: dropdownState.setIsOpen,
    mode,
    options,
    effectiveActiveIndex: dropdownState.effectiveActiveIndex,
    isActiveIndexControlled: dropdownState.isActiveIndexControlled,
    animateClose,
    closeOnSelect,
    dismissOnOutsideClick,
    keyboardNavigation,
    triggerDisabled,
    isSurfaceMeasured: positioning.isSurfaceMeasured,
    dropdownRef: positioning.dropdownRef,
    triggerRef,
    setInternalActiveIndex: dropdownState.setInternalActiveIndex,
    setHoveredIndex: dropdownState.setHoveredIndex,
    setActiveIndex: dropdownState.setActiveIndex,
  });

  return {
    onTriggerClick: interactions.onTriggerClick,
    shouldRenderSurface: dropdownState.isOpen,
    shouldRenderTrigger: mode === CDROPDOWN_MODE.Trigger && trigger,
    surfaceProps: buildDropdownSurfaceProps({
      align,
      options,
      selectedKey,
      hoveredIndex: dropdownState.hoveredIndex,
      effectiveActiveIndex: dropdownState.effectiveActiveIndex,
      maxHeight,
      scrollVerticalFadeTopSize,
      scrollVerticalFadeBottomSize,
      scrollVerticalFadeMiddleOpacity,
      scrollFadeSize,
      positioning,
      viewport,
      interactions,
    }),
    trigger,
    triggerRef,
    triggerStyle: buildTriggerStyle(triggerFullWidth),
  };
}

function buildTriggerStyle(triggerFullWidth: boolean): CSSProperties {
  return {
    display: "inline-flex",
    width: triggerFullWidth ? "100%" : "fit-content",
  };
}

function buildDropdownSurfaceProps(input: {
  align: DropdownSurfaceProps["align"];
  options: DropdownSurfaceProps["options"];
  selectedKey: DropdownSurfaceProps["selectedKey"];
  hoveredIndex: DropdownSurfaceProps["hoveredIndex"];
  effectiveActiveIndex: DropdownSurfaceProps["effectiveActiveIndex"];
  maxHeight: DropdownSurfaceProps["maxHeight"];
  scrollVerticalFadeTopSize: DropdownSurfaceProps["scrollVerticalFadeTopSize"];
  scrollVerticalFadeBottomSize: DropdownSurfaceProps["scrollVerticalFadeBottomSize"];
  scrollVerticalFadeMiddleOpacity: DropdownSurfaceProps["scrollVerticalFadeMiddleOpacity"];
  scrollFadeSize: number | undefined;
  positioning: ReturnType<typeof useDropdownPositioning>;
  viewport: ReturnType<typeof useDropdownOptionsViewport>;
  interactions: ReturnType<typeof useDropdownInteractions>;
}): Omit<DropdownSurfaceProps, "dataElScope"> {
  return {
    align: input.align,
    options: input.options,
    selectedKey: input.selectedKey,
    hoveredIndex: input.hoveredIndex,
    effectiveActiveIndex: input.effectiveActiveIndex,
    isAnimating: input.interactions.isAnimating,
    isClosing: input.interactions.isClosing,
    opensUpwards: input.positioning.opensUpwards,
    dropdownListStyle: input.positioning.dropdownListStyle,
    optionsWrapperStyle: input.viewport.optionsWrapperStyle,
    optionsViewportStyle: input.viewport.optionsViewportStyle,
    optionsViewportHeight: input.viewport.optionsViewportHeight,
    maxHeight: input.maxHeight,
    resolvedScrollFadeSize: input.scrollFadeSize ?? (input.maxHeight ? 10 : 0),
    scrollVerticalFadeTopSize: input.scrollVerticalFadeTopSize,
    scrollVerticalFadeBottomSize: input.scrollVerticalFadeBottomSize,
    scrollVerticalFadeMiddleOpacity: input.scrollVerticalFadeMiddleOpacity,
    optionsContainerRef: input.viewport.optionsContainerRef,
    optionsContentRef: input.viewport.optionsContentRef,
    onDropdownRef: input.positioning.onDropdownRef,
    onOptionElementRef: input.viewport.onOptionElementRef,
    onOptionMouseEnter: input.interactions.onOptionMouseEnter,
    onOptionMouseLeave: input.interactions.onOptionMouseLeave,
    onOptionClick: input.interactions.onOptionClick,
  };
}
